/**
 * ContributionChart (spec §4) — the X-ray.
 *
 * One row per dimension. Left to right: a 0–10 score bar (gradient fill,
 * muted when confidence < 0.5, with a hollow/half/filled confidence dot), a
 * diverging contribution bar whose length ∝ the component's actual
 * contribution to the composite (weights from input_formula.weightedComponents
 * — never the composite_weight column), and the +x.x readout.
 *
 * Rows sort by contribution, descending — the "which lever moves it" order.
 * The composite row is pinned on top, visually distinct. Unscored dimensions
 * render as designed gaps ("Education access — unscored"), never a zero,
 * never omitted (spec §6).
 *
 * Every row carries a visually-hidden text equivalent for the a11y tier
 * ("Infrastructure momentum, score 7.1, contributes +2.5 of 6.6"), and each
 * row is a button so the WHY panel is one interaction — keyboard included —
 * from any number (spec §12.4).
 */
import { areaXRayT, type AreaXRayLocale } from './copy'
import { formatScore, isProvisional, type Contribution } from './areaScoring'

interface UnscoredRow {
  slug: string
  label: string
}

interface ContributionChartProps {
  contributions: Contribution[]
  unscored: UnscoredRow[]
  compositeScore: number | null
  selectedSlug: string | null
  onSelect: (slug: string) => void
  dir: 'ltr' | 'rtl'
  locale: AreaXRayLocale
}

const SCORE_FILL = 'linear-gradient(90deg, var(--lc-action-secondary), var(--lc-accent))'
const SCORE_FILL_MUTED =
  'linear-gradient(90deg, var(--lc-surface-sunken), var(--lc-action-secondary))'

function ConfidenceDot({ confidence }: { confidence: number | null }) {
  // Glanceable honesty channel: hollow = low, half = medium, filled = high.
  const level =
    confidence == null || !Number.isFinite(confidence)
      ? 'none'
      : confidence >= 0.8
        ? 'high'
        : confidence >= 0.5
          ? 'medium'
          : 'low'
  const r = 5
  const c = 'var(--lc-text-muted)'
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      aria-hidden="true"
      data-confidence={level}
    >
      {level === 'high' && <circle cx={8} cy={8} r={r} fill={c} />}
      {level === 'medium' && (
        <>
          <path d={`M 8 ${8 - r} A ${r} ${r} 0 0 1 8 ${8 + r} Z`} fill={c} />
          <circle cx={8} cy={8} r={r} fill="none" stroke={c} strokeWidth={1.5} />
        </>
      )}
      {level === 'low' && <circle cx={8} cy={8} r={r} fill="none" stroke={c} strokeWidth={1.5} />}
      {level === 'none' && (
        <circle cx={8} cy={8} r={r} fill="none" stroke="var(--lc-border)" strokeWidth={1.5} strokeDasharray="2 2" />
      )}
    </svg>
  )
}

export function ContributionChart({
  contributions,
  unscored,
  compositeScore,
  selectedSlug,
  onSelect,
  dir,
  locale,
}: ContributionChartProps) {
  const sorted = [...contributions].sort((a, b) => b.contribution - a.contribution)
  const maxContribution = Math.max(...sorted.map((c) => c.contribution), 0.0001)
  const inlineStart = dir === 'rtl' ? 'right' : 'left'

  const rowBase: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 220px) 1fr minmax(120px, 200px) 56px',
    gap: 12,
    alignItems: 'center',
    width: '100%',
    textAlign: 'start',
    padding: '8px 10px',
    border: '1px solid transparent',
    borderRadius: 8,
    background: 'transparent',
    color: 'var(--lc-text-primary)',
    fontFamily: 'var(--lc-font-ui)',
    cursor: 'pointer',
  }

  return (
    <div role="list" data-testid="contribution-chart" data-dir={dir}>
      {/* Composite row — pinned on top, visually distinct. */}
      <div
        role="listitem"
        data-testid="composite-row"
        style={{
          ...rowBase,
          cursor: 'default',
          background: 'var(--lc-surface-raised)',
          borderColor: 'var(--lc-border)',
          fontWeight: 700,
        }}
      >
        <span style={{ color: 'var(--lc-text-heading)' }}>{areaXRayT('compositeRow', locale)}</span>
        <span aria-hidden="true" />
        <span
          aria-hidden="true"
          style={{
            height: 12,
            borderRadius: 6,
            width: `${compositeScore != null ? (compositeScore / 10) * 100 : 0}%`,
            background: SCORE_FILL,
          }}
        />
        <span
          data-testid="composite-score"
          style={{ fontFamily: 'var(--lc-font-mono)', color: 'var(--lc-text-heading)' }}
        >
          {formatScore(compositeScore)}
        </span>
        <span className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          {areaXRayT('compositeRow', locale)} {formatScore(compositeScore)}
        </span>
      </div>

      {sorted.map((entry) => {
        const provisional = isProvisional(entry.confidence)
        const lowConfidence = entry.confidence != null && entry.confidence < 0.5
        return (
          <div role="listitem" key={entry.slug}>
            <button
              type="button"
              data-testid={`row-${entry.slug}`}
              data-selected={selectedSlug === entry.slug}
              aria-pressed={selectedSlug === entry.slug}
              onClick={() => onSelect(entry.slug)}
              style={{
                ...rowBase,
                borderColor: selectedSlug === entry.slug ? 'var(--lc-focus-ring)' : 'transparent',
              }}
            >
            <span style={{ color: 'var(--lc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {entry.label}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ConfidenceDot confidence={entry.confidence} />
              <span
                aria-hidden="true"
                title={`${formatScore(entry.score)} / 10`}
                style={{
                  height: 10,
                  borderRadius: 5,
                  width: `${(entry.score / 10) * 100}%`,
                  background: lowConfidence || provisional ? SCORE_FILL_MUTED : SCORE_FILL,
                  opacity: lowConfidence ? 0.7 : 1,
                }}
              />
            </span>
            <span
              aria-hidden="true"
              style={{
                position: 'relative',
                height: 10,
                borderRadius: 5,
                background: 'var(--lc-surface-sunken)',
                overflow: 'hidden',
              }}
            >
              <span
                data-testid={`contribution-${entry.slug}`}
                style={{
                  position: 'absolute',
                  [inlineStart]: 0,
                  top: 0,
                  bottom: 0,
                  width: `${(entry.contribution / maxContribution) * 100}%`,
                  borderRadius: 5,
                  background: 'var(--lc-accent)',
                  opacity: provisional ? 0.55 : 1,
                }}
              />
            </span>
            <span
              data-testid={`readout-${entry.slug}`}
              style={{ fontFamily: 'var(--lc-font-mono)', color: 'var(--lc-text-heading)' }}
            >
              {areaXRayT('contributes', locale, { value: formatScore(entry.contribution) })}
            </span>
            <span className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
              {areaXRayT('contributionReadout', locale, {
                label: entry.label,
                score: formatScore(entry.score),
                contribution: formatScore(entry.contribution),
                composite: formatScore(compositeScore),
              })}
            </span>
            </button>
          </div>
        )
      })}

      {unscored.map((row) => (
        <div
          key={row.slug}
          role="listitem"
          data-testid={`unscored-${row.slug}`}
          style={{ ...rowBase, cursor: 'default', color: 'var(--lc-text-muted)' }}
        >
          <span>{row.label}</span>
          <span aria-hidden="true" style={{ height: 10, borderRadius: 5, background: 'transparent' }} />
          <span aria-hidden="true" />
          <span style={{ fontFamily: 'var(--lc-font-mono)' }}>—</span>
          <span className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            {`${row.label} — ${areaXRayT('unscored', locale)}`}
          </span>
        </div>
      ))}
    </div>
  )
}
